<template>
  <div
    class="dropdown-wrapper"
    :class="{ 'dropdown-wrapper--error': errors.length }"
  >
    <label
      v-if="label"
      class="dropdown-wrapper__label flex items-center gap-1"
      :for="name"
    >
      {{ label }} <span v-if="errorMessage?.trim()">- {{ errorMessage }}</span>
      <UITooltip
        v-if="tooltip"
        content-width="230px"
      >
        <UIIcon
          size="xs"
          name="info-circle"
        ></UIIcon>
        <template #content>
          {{ tooltip }}
        </template>
      </UITooltip>
    </label>
    <div
      ref="dropdown"
      class="dropdown"
      :class="[
        { 'dropdown--focused': isMenuVisible },
        { 'dropdown--selected-value': inputValue },
        { 'dropdown--disabled': disabled },
        { 'dropdown--loading': loading },
        { 'dropdown--compact': compact },
        { 'dropdown--borderless': borderless },
      ]"
    >
      <UIIcon
        v-if="leftIcon"
        class="dropdown__left-icon"
        :name="leftIcon"
      />
      <UIDropdownValue
        :multiple="multiple"
        :value="inputValue"
        :label-key="labelKey"
        :items="items"
        :persistent-placeholder="persistentPlaceholder"
        :value-key="valueKey"
        :disabled="disabled"
        @focus="setFocus"
        @clear="handleClear"
      />
      <input
        :id="`${name}-dropdown`"
        ref="input"
        v-model="search"
        autocomplete="off"
        type="text"
        class="dropdown__input"
        :disabled="disabled"
        :class="{ 'dropdown__input--persistent-placeholder': getIsPersistentPlaceholderActive() }"
        :placeholder="getPlaceholder()"
        @focus="handleFocus"
        @input="handleInput"
        @keydown.backspace="handleBackspace"
      />
      <UIBaseTransition>
        <div
          v-if="loading"
          class="dropdown__loader"
        >
          <UIProgressSpinner
            width="20"
            height="20"
          />
        </div>
        <div
          v-else-if="removable"
          class="dropdown__remove"
          @click.stop="handleRemoveClick"
        >
          <UIIcon name="big-close-circle-filled" />
        </div>
        <div
          v-else
          class="dropdown__chevron"
          :class="{ 'dropdown__chevron--open': isMenuVisible }"
          @click="handleChevronClick"
        >
          <UIIcon name="chevron-big-filled-down" />
        </div>
      </UIBaseTransition>
    </div>
    <UIBaseTransition>
      <UIMenu
        v-if="isMenuVisible"
        ref="menu"
        :multiple
        :model-value="inputValue"
        :search-value="search"
        :menu-visible="isMenuVisible"
        :style="floatingStyles"
        :value-key
        :menu-width
        :menu-height="menuHeight"
        :loading
        :searchable="searchable"
        :items="filteredItems"
        :search-placeholder
        :search-key
        :item-size
        :is-loading-more="props.isLoadingMore"
        :has-more="props.hasMore"
        :on-load-more="props.onLoadMore"
        full-width
      >
        <template #default="{ item, active }">
          <UIMenuOption
            :multiple
            :active
            :item
            :avatar
            :label-key
            @select="handleSelect($event)"
          >
            <slot
              :active
              :item
            />
          </UIMenuOption>
        </template>
        <template
          v-if="loading"
          #custom
        >
          {{ t('common.actions.loading') }}
        </template>
      </UIMenu>
    </UIBaseTransition>
  </div>
</template>

<script setup lang="ts">
import { useFloating, autoUpdate, flip, offset, type Placement } from '@floating-ui/vue';
import { useField } from 'vee-validate';
import { useDebounceFn } from '@vueuse/core';
import isArray from 'lodash/isArray';
import type { MenuOptionProps } from '../menu/Option.vue';

import type { MenuProps } from '../../../types/ui';
import type { UIMenu } from '#build/components';

const { t } = useI18n();

export type DropdownProps = {
  items?: any[];
  modelValue?: any;
  name: string;
  multiple?: boolean;
  disabled?: boolean;
  syncVModel?: boolean;
  persistentPlaceholder?: boolean;
  valueKey?: string;
  labelKey?: string;
  label?: string;
  placeholder: string;
  loading?: boolean;
  leftIcon?: string;
  clearable?: boolean;
  compact?: boolean;
  borderless?: boolean;
  removable?: boolean;
  tooltip?: string;
  menuWidth?: string;
  strategy?: string;
  defaultLimit?: number;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
} & Omit<MenuProps, 'menuVisible'>;

const props = withDefaults(defineProps<DropdownProps>(), {
  items: () => [],
  modelValue: undefined,
  label: '',
  multiple: false,
  syncVModel: false,
  persistentPlaceholder: false,
  menuWidth: '',
  menuHeight: undefined,
  disabled: false,
  loading: false,
  leftIcon: '',
  clearable: true,
  labelKey: 'name',
  valueKey: 'id',
  tooltip: '',
  strategy: 'absolute',
  borderless: false,
  defaultLimit: undefined,
  isLoadingMore: false,
  hasMore: false,
  onLoadMore: undefined,
});

defineSlots<{
  default: (props: MenuOptionProps) => any;
}>();

const emit = defineEmits<{
  (e: 'clear'): void;
  (e: 'remove'): void;
  (e: 'update:modelValue', value: any): void;
  (e: 'update:search', value: string): void;
}>();

const name = toRef(props, 'name');

const {
  value: inputValue,
  errorMessage,
  errors,
  handleChange,
} = useField<string | number | number[] | undefined>(name, undefined, {
  initialValue: props.modelValue,
  syncVModel: props.syncVModel,
});

const { isMobile } = useAppBreakpoints();

const shallowInputValue = ref<number[]>([]);

const menu = ref<InstanceType<typeof UIMenu> | null>(null);

const handleSelect = (value: any) => {
  const valueFormatted = value[props.valueKey];

  if (props.multiple) {
    if (isArray(inputValue.value)) {
      const isValuePresent = inputValue.value.includes(valueFormatted);

      if (isValuePresent) {
        // Remove from existing
        handleChange(inputValue.value.filter((id: number) => id !== valueFormatted));
      } else {
        // Add to existing
        handleChange([...inputValue.value, valueFormatted]);
      }
    } else {
      // Create new one
      handleChange([valueFormatted]);
    }

    search.value = '';
    debouncedSearch.value = '';

    if (menu.value && menu.value.list) {
      menu.value.list.scrollTop = 0;
    }

    setFocus();

    return;
  } else {
    const isValueEqual = valueFormatted == inputValue.value;

    handleChange(isValueEqual && props.clearable ? undefined : valueFormatted);
  }

  isMenuVisible.value = false;
  search.value = '';
};

const getPlaceholder = () => {
  if (props.multiple && isArray(inputValue.value) && props.persistentPlaceholder) {
    if (isMenuVisible.value && search.value) {
      return '';
    }
    return props.placeholder;
  }

  if (props.loading) return props.placeholder;

  if (isMenuVisible.value && (search.value || debouncedSearch.value)) {
    return '';
  }

  return inputValue.value ? '' : props.placeholder;
};

const getIsPersistentPlaceholderActive = () => {
  if (props.multiple && isArray(inputValue.value)) {
    return Boolean(inputValue.value.length);
  }

  return false;
};

const handleClear = () => {
  handleChange(undefined);
  search.value = '';
  debouncedSearch.value = '';
  isMenuVisible.value = false;

  emit('clear');
};

const handleBackspace = (event: KeyboardEvent) => {
  const value = (event.target as HTMLInputElement).value;

  if (!value && props.clearable) {
    if (props.multiple) {
      handleChange(inputValue.value ? (inputValue.value as number[]).slice(0, -1) : undefined);
      return;
    }

    handleChange(undefined);
  }
};

const input = ref<HTMLInputElement | null>(null);

const setFocus = () => {
  input.value?.focus();
};

const handleFocus = () => {
  if (isMobile && props.multiple) {
    shallowInputValue.value = isArray(inputValue.value) ? (inputValue.value as number[]) : [];

    // Popup is visible, set focus on search input
    nextTick(() => {
      if (menu.value) {
        if (menu.value.searchRef?.input?.input) {
          menu.value.searchRef.input.input.focus();
        }
      }
    });
  }

  if (!isMobile) {
    isMenuVisible.value = true;
  }

  setFocus();
};

const handleInput = (event: Event) => {
  const value = (event.target as HTMLInputElement).value;

  if (isMobile) {
    if (value.length >= 2) {
      isMenuVisible.value = true;
    } else {
      // Just for animation
      setTimeout(() => {
        isMenuVisible.value = false;
      }, 50);
    }
  }
};

const handleChevronClick = () => {
  isMenuVisible.value = !isMenuVisible.value;

  if (isMenuVisible.value) {
    setFocus();
  }
};

const handleRemoveClick = () => {
  isMenuVisible.value = false;

  emit('remove');
};

const search = ref('');
const debouncedSearch = ref('');

const { filter } = useFilterItems();

const updateDebouncedSearch = useDebounceFn((value: string) => {
  debouncedSearch.value = value;
}, 150);

watch(
  search,
  (newValue) => {
    emit('update:search', newValue);
    if (!newValue.trim()) {
      debouncedSearch.value = '';
      return;
    }
    updateDebouncedSearch(newValue.trim());
  },
  { immediate: false },
);

const filteredItems = computed(() => {
  if (!props.items || !props.items.length) {
    return [];
  }

  if (props.onLoadMore) {
    const array = inputValue.value;
    if (props.multiple && isArray(array) && array.length > 0) {
      const values = array;
      return [...props.items].sort((a, b) => values.indexOf(b[props.valueKey]) - values.indexOf(a[props.valueKey]));
    }
    return props.items;
  }

  let itemsToShow: any[] = [];
  const maxResults = 20;

  if (!debouncedSearch.value || !debouncedSearch.value.trim()) {
    if (props.defaultLimit && props.items.length > props.defaultLimit) {
      itemsToShow = props.items.slice(0, props.defaultLimit);
    } else {
      itemsToShow = props.items;
    }
  } else {
    const filtered = filter(props.items, debouncedSearch.value, props.searchKey);
    itemsToShow = filtered.slice(0, maxResults);
  }

  const array = inputValue.value;

  if (props.multiple && isArray(array) && array.length > 0) {
    const values = array;
    return [...itemsToShow].sort((a, b) => values.indexOf(b[props.valueKey]) - values.indexOf(a[props.valueKey]));
  }
  return itemsToShow;
});

const dropdown = ref();

const isMenuVisible = ref(false);

onClickOutside(
  menu,
  () => {
    isMenuVisible.value = false;
    search.value = '';
    debouncedSearch.value = '';
  },
  { ignore: [dropdown] },
);

const isMounted = ref(false);

onMounted(() => {
  // To prevent teleport error warning
  setTimeout(() => {
    isMounted.value = true;
  }, 500);
});

const { floatingStyles } = useFloating(dropdown, menu, {
  placement: 'bottom-start',
  whileElementsMounted: autoUpdate,
  strategy: computed(() => props.strategy as 'fixed' | 'absolute'),
  middleware: [
    flip({
      fallbackPlacements: isMobile ? ['top-start'] : ['bottom-start', 'top-start', 'bottom-end', 'top-end'],
    }),
  ],
  open: isMenuVisible,
});
</script>

<style scoped lang="scss">
@use '../../../styles/variables.scss' as *;

.dropdown {
  padding: 8px 16px;
  border-radius: $base-border-radius;
  border: $base-border-width solid $black-20;
  height: 48px;
  display: flex;
  align-items: center;
  transition: $base-transition all;
  background: white;
  color: $black-80;

  &__chevron,
  &__remove {
    display: flex;
    color: $black-60;
    margin-inline-start: 4px;
    flex-shrink: 0;
    transition: $base-transition all;
    cursor: pointer;
    user-select: none;

    &--open {
      transform: rotate(180deg);
    }
  }

  &__left-icon {
    display: flex;
    color: $black-60;
    width: 24px;
    height: 24px;
    margin-inline-end: 8px;
  }

  &__loader {
    width: 20px;
    height: 20px;
    color: $black-30;
  }

  &-wrapper {
    font-size: 14px;
    line-height: 140%;
    color: $black-80;
    position: relative;

    &__label {
      margin-bottom: 2px;
      margin-inline-start: 16px;
      font-weight: 500;
    }

    &--error {
      .dropdown {
        border: $base-border-width solid $error-100;
      }

      .dropdown-wrapper__label {
        color: $error-100;
      }

      @media screen and (max-width: 1023px) {
        .dropdown__input {
          &:placeholder-shown,
          &::placeholder {
            color: $error-100;
          }
        }
      }
    }
  }

  &:hover {
    border: $base-border-width solid $black-60;
  }

  &--compact {
    height: 40px;
    padding: 8px 12px;
  }

  &--borderless {
    border: none;
    background: #f8f7f3;

    &:hover {
      border: none;
    }

    &.dropdown--focused {
      border: none;
    }
  }

  &-wrapper--error .dropdown--borderless {
    border: none;
  }

  &--disabled {
    background: $black-05;
    border: $base-border-width solid $black-20 !important;
    color: $black-60;

    .dropdown__chevron {
      color: $black-60;
      pointer-events: none;
    }
  }

  &--focused {
    border: $base-border-width solid $black-100 !important;

    .dropdown__chevron {
      color: $black-100;
    }
  }

  &__input {
    width: 100%;
    border: 0;
    outline: none;
    font-size: 14px;
    transition:
      $base-transition background,
      $base-transition color;
    background: transparent;

    @media screen and (max-width: 1023px) {
      font-size: 16px;
    }

    &:disabled {
      background: $black-05;
      color: $black-60;

      &::placeholder {
        color: $black-60;
      }
    }

    &::placeholder {
      color: $black-40;
    }

    &--persistent-placeholder {
      &::placeholder {
        color: $black-100;
      }
    }

    &:placeholder-shown {
      text-overflow: ellipsis;
    }
  }

  &__mobile-popup {
    &-body {
      height: 100%;
    }

    :deep() {
      .popup {
        &__content {
          height: calc(100% - 32px);

          &-body {
            height: 100%;
          }

          .menu {
            box-shadow: none;
            border-radius: 0;
            border: none;
            height: 100%;
            width: 100%;
            padding: 0;

            &__list {
              margin-top: 4px;
            }
          }
        }
      }
    }
  }
}
</style>
